/**
 * O ANFITRIÃO DO AVISO DE MUDANÇA — sem tela. Mostra UM aviso por vez, uma vez por USUÁRIO.
 *
 * Só aparece quando não disputa atenção: tour completo já fechado (a primeira impressão é
 * dele), nenhum tour aberto, Ajuda fechada, nenhum diálogo e o alvo de fato na tela. Se o
 * alvo não aparece em 10s, desiste em silêncio e NÃO grava nada: tenta na próxima montagem.
 * Fechar por qualquer caminho (Entendi, X, Esc) marca como visto. O progresso é do usuário
 * (norma do convidado): quem entra em base existente também vê.
 */
import { useEffect, useRef } from 'react'
import { useLocation } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import type { TFunction } from 'i18next'
import { useGuidedTourStore } from '@/stores/guidedTourStore'
import { AVISOS, chavesDoAviso, proximoAviso, type AvisoDeMudanca } from './avisos'
import { esperarElemento } from './esperar-elemento'
import { abrirTour, type TourAberto } from './motor-do-tour'
import type { RotulosDoTour } from './types'

/** Respiro depois da tela assentar, antes de o aviso aparecer. */
export const RESPIRO_DO_AVISO_MS = 1500
/** Quanto esperar o alvo existir antes de desistir em silêncio. */
export const ESPERA_DO_ALVO_MS = 10000

/** Diálogo da própria aplicação (o balão do Driver também é `dialog`, por isso fica de fora). */
const DIALOGO_ABERTO = '[role="dialog"]:not(#driver-popover-content)'

function livreParaAvisar(): boolean {
  const s = useGuidedTourStore.getState()
  if (!s.userId || s.registro === null || s.ativo || s.ajudaAberta) return false
  return document.querySelector(DIALOGO_ABERTO) === null
}

function rotulosDoAviso(t: TFunction): RotulosDoTour {
  return {
    proximo: t('guidedTour.avisos.entendi'),
    voltar: t('guidedTour.botoes.voltar'),
    concluir: t('guidedTour.avisos.entendi'),
    progresso: t('guidedTour.botoes.progresso'),
    fechar: t('guidedTour.avisos.fechar'),
  }
}

interface Props {
  /** Registro de avisos; o padrão é o real. Existe para provar que um aviso novo não muda o anfitrião. */
  registro?: readonly AvisoDeMudanca[]
}

export function AvisoDeMudancaHost({ registro = AVISOS }: Props = {}): null {
  const { t } = useTranslation()
  const { pathname } = useLocation()
  const userId = useGuidedTourStore((s) => s.userId)
  const concluiuTour = useGuidedTourStore((s) => s.registro !== null)
  const ativo = useGuidedTourStore((s) => s.ativo)
  const ajudaAberta = useGuidedTourStore((s) => s.ajudaAberta)
  const vistos = useGuidedTourStore((s) => s.avisosVistos)
  const aberto = useRef<TourAberto | null>(null)

  useEffect(() => {
    const aviso = proximoAviso(vistos, registro)
    if (!aviso || !userId || !concluiuTour || ativo || ajudaAberta || aberto.current) return
    let vivo = true
    const timer = window.setTimeout(() => {
      aviso.prepara?.()
      void esperarElemento(aviso.alvo, ESPERA_DO_ALVO_MS).then((alvo) => {
        if (!vivo || !alvo || aberto.current || !livreParaAvisar()) return
        const k = chavesDoAviso(aviso.id)
        const store = useGuidedTourStore.getState()
        store.marcarAtivo(true)
        aberto.current = abrirTour({
          passos: [
            {
              id: `aviso-${aviso.id}`,
              alvo: aviso.alvo,
              titulo: t(k.titulo),
              texto: t(k.texto),
              lado: aviso.lado,
              avanco: { tipo: 'botao' },
              rotuloProximo: t('guidedTour.avisos.entendi'),
            },
          ],
          rotulos: rotulosDoAviso(t),
          irPara: () => undefined,
          semProgresso: true,
          aoFechar: () => {
            aberto.current = null
            useGuidedTourStore.getState().marcarAvisoVisto(aviso.id)
            useGuidedTourStore.getState().definirFechamento(null)
            useGuidedTourStore.getState().marcarAtivo(false)
          },
        })
        store.definirFechamento(aberto.current.fechar)
      })
    }, RESPIRO_DO_AVISO_MS)
    return () => {
      vivo = false
      window.clearTimeout(timer)
    }
  }, [userId, concluiuTour, ativo, ajudaAberta, vistos, pathname, registro, t])

  useEffect(() => () => aberto.current?.fechar(), [])
  return null
}
